/**
 * Load and set up routes and authentication
 */
import Vue from 'vue'
import 'es6-promise/auto'
import VueAuth from '@websanova/vue-auth'
import auth from './auth'
import VueRouter from 'vue-router'
import VueAxios from 'vue-axios';
import axios from 'axios';
import App from './App.vue';
import routes from './routes.js'
import Cookies from 'js-cookie'

// Set Vue router, axios, and auth
const router = new VueRouter({mode: 'history', routes: routes});
Vue.router = router
Vue.use(VueRouter)

// Set Vue authentication
Vue.use(VueAxios, axios)

// Axios is globally set on this.$http
//Vue.prototype.$http = axios;

axios.defaults.baseURL = `${process.env.MIX_APP_URL}/api`
Vue.use(VueAuth, auth)

// Use cookies (e.g. for clear cache message)
import VueCookie from 'vue-cookie';
Vue.use(VueCookie);

// Set Transitions
import VuePageTransition from 'vue-page-transition'
Vue.use(VuePageTransition)

// Set Google Tag Manager
import VueGtm from '@gtm-support/vue2-gtm';
Vue.use(VueGtm, {
    id: 'GTM-M89X78K',
});

/**
 * Load and set up form validation
 */

import {
    ValidationObserver,
    ValidationProvider,
    extend,
    localize
} from "vee-validate";
import en from "vee-validate/dist/locale/en.json";
import * as rules from "vee-validate/dist/rules";

// Configure vee-validate
import { configure } from 'vee-validate';

const config = {
    mode: 'eager'
};
configure(config);

// Install VeeValidate rules and localization
Object.keys(rules).forEach(rule => {
    extend(rule, rules[rule]);
});

// additional rules
extend('url', {
    validate: (str) => {
        // Difficult topic apparently https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
        // Source: https://gist.github.com/dperini/729294
        // Tested: https://mathiasbynens.be/demo/url-regex (all are accepted, most errors are resolved)

        let pattern = new RegExp(
            "^" +
            // protocol identifier (optional)
            // short syntax // still required
            "(?:(?:(?:https?|ftp):)?\\/\\/)" +
            // user:pass BasicAuth (optional)
            "(?:\\S+(?::\\S*)?@)?" +
            "(?:" +
            // IP address exclusion
            // private & local networks
            "(?!(?:10|127)(?:\\.\\d{1,3}){3})" +
            "(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})" +
            "(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})" +
            // IP address dotted notation octets
            // excludes loopback network 0.0.0.0
            // excludes reserved space >= 224.0.0.0
            // excludes network & broadcast addresses
            // (first & last IP address of each class)
            "(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" +
            "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}" +
            "(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))" +
            "|" +
            // host & domain names, may end with dot
            // can be replaced by a shortest alternative
            // (?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+
            "(?:" +
            "(?:" +
            "[a-z0-9\\u00a1-\\uffff]" +
            "[a-z0-9\\u00a1-\\uffff_-]{0,62}" +
            ")?" +
            "[a-z0-9\\u00a1-\\uffff]\\." +
            ")+" +
            // TLD identifier name, may end with dot
            "(?:[a-z\\u00a1-\\uffff]{2,}\\.?)" +
            ")" +
            // port number (optional)
            "(?::\\d{2,5})?" +
            // resource path (optional)
            "(?:[/?#]\\S*)?" +
            "$", "i"
        );
        return !!pattern.test(str);
    },
    message: 'This is not a valid URL'
})
extend('hex', {
    validate: (str) => {
        var pattern = new RegExp('^#[a-zA-Z0-9]{6}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid Hex color code.'
})
extend('landphone', {
    validate: (str) => {
        var pattern = new RegExp('^\\+[0-9]{4,20}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid phone number. Start with the plus sign then landcode. Don\'t use spaces or dashes.'
})
extend('social_username', {
    validate: (str) => {
        var pattern = new RegExp('^[a-zA-Z0-9_.\-]{2,50}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid username.'
})
extend('skype_username', {
    validate: (str) => {
        var pattern = new RegExp('^[a-zA-Z0-9_:.\-]{2,50}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid Skype username.'
})
extend('linkedin', {
    validate: (str) => {
        var pattern = new RegExp('^(in\/|company\/|groups\/)[a-zA-Z0-9_.\-]{2,50}(\/){0,1}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid LinkedIn page.'
})
extend('youtube', {
    validate: (str) => {
        var pattern = new RegExp('^(channel\/|c\/)[a-zA-Z0-9_.\-]{2,50}(\/){0,1}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid YouTube page.'
})
extend('youtube_video_id', {
    validate: (str) => {
        var pattern = new RegExp('^[a-zA-Z0-9_.-]{2,50}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid YouTube video.'
})
extend('facebook_page', {
    validate: (str) => {
        var pattern = new RegExp('^(groups\/){0,1}[a-zA-Z0-9_.\-]{2,50}(\/){0,1}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid Facebook page.'
})
extend('calendly', {
    validate: (str) => {
        var pattern = new RegExp('^[a-zA-Z0-9-_.\/]{3,50}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid Calendly suffix.'
})
extend('domain_folders', {
    validate: (str) => {
        var pattern = new RegExp('^[A-Za-z0-9-_.\/]{3,100}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid reference.'
})
extend('domain', {
    validate: (str) => {
        var pattern = new RegExp('^(?!\\-)(?:(?:[a-zA-Z\\d][a-zA-Z\\d\\-]{0,61})?[a-zA-Z\\d]\\.){1,126}(?!\\d+)[a-zA-Z\\d]{1,63}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid domain.'
})
extend('hide_on_pages', {
    validate: (str) => {
        var pattern = new RegExp('^[a-z0-9\*.\?#&+=%$!_\/\-]{1,200}$');
        return !!pattern.test(str);
    },
    message: 'This is not a valid page reference.'
})
extend('time', {
    validate: (str) => {
        var pattern = new RegExp('^[0-9]{2}:[0-9]{2}$');
        return !!pattern.test(str);
    },
    message: 'Enter a valid time (24h format).'
})
// Create required unless rule where the field is required unless another field is filled.
// Reference the field with @ to get field contents. Example usage: required_unless:@field,null,Error message
extend('required_unless', {
    params: ['target1', 'target2', 'message'], // targets can be null
    validate(value, { target1, target2 }) {
        if(target2 == null) {
            return {
                required: (
                    ['', null, undefined].indexOf(value) === -1 // required if current field has value
                    || ! ['', null, undefined].indexOf(target1) === -1 // not required if other is filled
                ),
                valid: (
                    ['', null, undefined].indexOf(value) === -1 // valid if current field has value
                    || ['', null, undefined].indexOf(target1) === -1 // this field is valid if other is filled
                )
            };
        } else { // there are two targets
            return {
                required: (
                    ['', null, undefined].indexOf(value) === -1 // required if current field has value
                    || ! ['', null, undefined].indexOf(target1) === -1 // not required if other is filled
                    || ! ['', null, undefined].indexOf(target2) === -1 // not required if other is filled
                ),
                valid: (
                    ['', null, undefined].indexOf(value) === -1 // valid if current field has value
                    || ['', null, undefined].indexOf(target1) === -1 // this field is valid if other is filled
                    || ['', null, undefined].indexOf(target2) === -1 // this field is valid if other is filled
                )
            };
        }
    },
    message: '{message}', // fully dynamic message
    computesRequired: true
});


localize("en", en);

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);


/**
 * Check for Affiliate
 */
const via = new URL(location.href).searchParams.get('via')
if (via) {
    Cookies.set('boei_affiliate', via, {
        expires: 30,
        domain: '.boei.help',
        secure: true,
        sameSite: 'lax',
    })
}


/**
 * Load Bootstrap Vue
 */
import {BootstrapVue, IconsPlugin} from 'bootstrap-vue'

// Install BootstrapVue
Vue.use(BootstrapVue)
// Optionally install the BootstrapVue icon components plugin
Vue.use(IconsPlugin)

// Install copy to clipboard
import VueClipboard from 'vue-clipboard2'
Vue.use(VueClipboard);

// Vue confetti
import VueConfetti from 'vue-confetti';
Vue.use(VueConfetti);

// Toggle button
import ToggleButton from 'vue-js-toggle-button'
Vue.use(ToggleButton)

/**
 * Create a fresh Vue application instance and attach it to the page.
 */

const app = new Vue(Vue.util.extend({router}, App)).$mount('#app');
